import { TASK_TYPES, TASK_TYPE_LABELS, type TaskType } from '@hearth/shared';
import { clsx } from 'clsx';
import { Clock, X } from 'lucide-react';
import { type FormEvent, useId, useState } from 'react';
import { Button, IconButton, Switch } from '../../components/ui';
import { defaultTitle } from '../tasks/entryModel';
import { TASK_TYPE_ICONS } from '../tasks/taskAppearance';
import { CalendarPopover } from './CalendarPopover';
import { type DraftSlot, describeWhen, toggleSlotAllDay } from './model';
import type { NewEntry } from './useCalendarActions';

/** Type of an entry created by clicking the grid: the CRM's default next step. */
export const QUICK_CREATE_TYPE: TaskType = 'follow_up';

export type QuickCreatePopoverProps = {
  slot: DraftSlot;
  /** The draft block in the time grid or the month cell; null centers the popover. */
  anchor: HTMLElement | null;
  now: Date;
  busy: boolean;
  /** What is typed so far; the page also draws it on the draft block. */
  title: string;
  onTitleChange: (title: string) => void;
  onSlotChange: (slot: DraftSlot) => void;
  onSave: (entry: NewEntry) => void;
  /** Continue in the full editor with what was typed so far. */
  onMoreOptions: (entry: NewEntry) => void;
  onClose: () => void;
};

/** Google-style quick create: a title, the type and the slot that was clicked; everything else is "More options". */
export function QuickCreatePopover({
  slot,
  anchor,
  now,
  busy,
  title,
  onTitleChange,
  onSlotChange,
  onSave,
  onMoreOptions,
  onClose,
}: QuickCreatePopoverProps) {
  const [type, setType] = useState<TaskType>(QUICK_CREATE_TYPE);
  const typeLabelId = useId();
  const entry = (): NewEntry => ({ type, title: title.trim() || defaultTitle(type), slot });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onSave(entry());
  };

  return (
    <CalendarPopover anchor={anchor} onClose={onClose} label="New entry" className="w-[24rem]">
      <form onSubmit={submit}>
        <div className="flex justify-end px-2 pt-2">
          <IconButton icon={X} label="Close" size="sm" onClick={onClose} data-focus-skip />
        </div>
        <div className="space-y-3 px-4 pb-4">
          <input
            data-autofocus
            aria-label="Title"
            value={title}
            onChange={(event) => onTitleChange(event.target.value)}
            placeholder={`Add title (${defaultTitle(type)})`}
            className="w-full border-b border-slate-200 bg-transparent pb-1.5 text-base text-slate-900 placeholder:text-slate-400 focus:border-ember-500 focus:outline-none"
          />

          <div>
            <p id={typeLabelId} className="sr-only">
              Type
            </p>
            <div role="group" aria-labelledby={typeLabelId} className="flex flex-wrap gap-1.5">
              {TASK_TYPES.map((value) => {
                const Icon = TASK_TYPE_ICONS[value];
                const active = type === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setType(value)}
                    className={clsx(
                      'inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500',
                      active
                        ? 'bg-ember-600 text-white'
                        : 'bg-surface text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100',
                    )}
                  >
                    <Icon size={13} aria-hidden />
                    {TASK_TYPE_LABELS[value]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-start gap-3 text-[13px] text-slate-700">
            <Clock size={15} aria-hidden className="mt-0.5 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1 space-y-2">
              <p>
                {describeWhen(
                  { dueAt: slot.start.toISOString(), endAt: slot.end.toISOString(), allDay: slot.allDay },
                  now,
                )}
              </p>
              <Switch
                size="sm"
                checked={slot.allDay}
                onChange={() => onSlotChange(toggleSlotAllDay(slot))}
                label="All day"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            <Button variant="ghost" size="sm" onClick={() => onMoreOptions(entry())}>
              More options
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={busy}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </CalendarPopover>
  );
}
